import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import { Button } from '../ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { Trophy, LogOut, User, Settings, ChevronDown } from 'lucide-react';
import ProfileModal from '../shared/ProfileModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * DashboardHeader - Header component for authenticated users
 * Shows user info, team affiliation, points, and logout button
 * Profile access via avatar dropdown menu (modern UI pattern)
 */
const DashboardHeader = ({ onProfileUpdate }) => {
  const { user, logout, refreshUser } = useAuth();
  const [userTeams, setUserTeams] = useState([]);
  const [showProfileModal, setShowProfileModal] = useState(false);
  
  // Compute full name from first_name and last_name, with fallback
  const userFullName = user?.first_name && user?.last_name 
    ? `${user.first_name} ${user.last_name}`.trim()
    : user?.full_name || user?.username || 'User';
  
  useEffect(() => {
    const fetchUserTeams = async () => {
      if (user?.teams && user.teams.length > 0) {
        try {
          const response = await axios.get(`${API}/teams`);
          const allTeams = response.data;
          const myTeams = allTeams.filter(team => user.teams.includes(team.id));
          setUserTeams(myTeams);
        } catch (error) {
          console.error('Failed to fetch user teams');
        }
      }
    };
    fetchUserTeams();
  }, [user]);

  const handleProfileUpdate = async () => {
    await refreshUser();
    if (onProfileUpdate) onProfileUpdate();
  };
  
  return (
    <>
      <div className="bg-blue-900/95 backdrop-blur-sm border border-blue-700 border-b border-emerald-100 p-4">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <div className="flex items-center space-x-4">
            {/* Display club icons for user's teams */}
            {userTeams.length > 0 && userTeams[0].club_icon ? (
              <img 
                src={`${API}/teams/${userTeams[0].id}/club-icon?t=${Date.now()}`}
                alt="Club icon"
                className="w-12 h-12 rounded-lg object-cover border-2 border-yellow-500"
                onError={(e) => {
                  // Fallback to Trophy icon if image fails to load
                  e.target.style.display = 'none';
                  e.target.nextElementSibling.style.display = 'flex';
                }}
              />
            ) : null}
            <div 
              className="w-12 h-12 bg-gradient-to-r from-slate-9000 to-teal-600 rounded-lg flex items-center justify-center"
              style={{ display: userTeams.length > 0 && userTeams[0].club_icon ? 'none' : 'flex' }}
            >
              <Trophy className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-white">Grind.365</h1>
              <p className="text-sm text-yellow-400">
                {userTeams.length > 0 ? `${userTeams[0].name} • ` : ''}
                Level {user?.level} • {user?.total_points} points
              </p>
            </div>
          </div>
          
          {/* User Profile Dropdown */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button 
                variant="ghost" 
                data-testid="profile-menu-trigger"
                className="flex items-center space-x-3 hover:bg-blue-800/50 rounded-lg px-3 py-2 h-auto"
              >
                <div className="text-right hidden sm:block">
                  <p className="font-medium text-white">{userFullName}</p>
                  <p className="text-sm text-yellow-400 capitalize">{user?.role}</p>
                </div>
                <Avatar className="w-10 h-10 rounded-lg border-2 border-yellow-500/50 hover:border-yellow-500 transition-colors">
                  {user?.profile_image ? (
                    <AvatarImage 
                      src={`${API}/user/${user.id}/profile-image?t=${Date.now()}`}
                      alt={userFullName}
                      className="rounded-lg"
                    />
                  ) : null}
                  <AvatarFallback className="bg-gradient-to-br from-yellow-500 to-yellow-600 text-blue-900 font-bold rounded-lg">
                    {userFullName?.charAt(0) || 'A'}
                  </AvatarFallback>
                </Avatar>
                <ChevronDown className="w-4 h-4 text-yellow-400" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent 
              align="end" 
              className="w-56 bg-blue-900 border border-blue-700 text-white"
            >
              <DropdownMenuLabel className="text-yellow-400">
                <div className="flex flex-col space-y-1">
                  <p className="text-sm font-medium text-white">{userFullName}</p>
                  <p className="text-xs text-sky-300">@{user?.username}</p>
                </div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator className="bg-blue-700" />
              <DropdownMenuItem 
                onClick={() => setShowProfileModal(true)}
                data-testid="profile-menu-item"
                className="cursor-pointer hover:bg-blue-800 focus:bg-blue-800 focus:text-white"
              >
                <User className="mr-2 h-4 w-4 text-yellow-400" />
                <span>Profile</span>
              </DropdownMenuItem>
              <DropdownMenuItem 
                onClick={() => setShowProfileModal(true)}
                className="cursor-pointer hover:bg-blue-800 focus:bg-blue-800 focus:text-white"
              >
                <Settings className="mr-2 h-4 w-4 text-sky-400" />
                <span>Settings</span>
              </DropdownMenuItem>
              <DropdownMenuSeparator className="bg-blue-700" />
              <DropdownMenuItem 
                onClick={logout}
                data-testid="logout-button"
                className="cursor-pointer hover:bg-red-900/50 focus:bg-red-900/50 text-red-400 focus:text-red-400"
              >
                <LogOut className="mr-2 h-4 w-4" />
                <span>Log out</span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Profile Modal */}
      <ProfileModal 
        open={showProfileModal}
        onOpenChange={setShowProfileModal}
        user={user}
        onUpdate={handleProfileUpdate}
      />
    </>
  );
};

export default DashboardHeader;
